/**
 * ProfileArticlesSection — reusable articles surface for SocialPulse
 * profile pages (/:handle).
 *
 * Two variants:
 *   • variant="strip"  — horizontal preview strip (3-6 cards) for
 *                        embedding on the main "My Pulse" tab
 *   • variant="grid"   — full grid for the "Articles" tab
 *
 * Backend: GET /api/publisher/v1/authors/{author_id}/articles
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, PenLine, Eye, Heart, Newspaper, ArrowRight } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;

function useAuthorArticles(authorId, limit = 20) {
  const [state, setState] = useState({ loading: true, articles: [], error: null });
  useEffect(() => {
    if (!authorId) return;
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${BASE}/authors/${authorId}/articles?limit=${limit}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (alive) setState({ loading: false, articles: d.articles || [], error: null });
      } catch (e) {
        if (alive) setState({ loading: false, articles: [], error: e.message });
      }
    })();
    return () => { alive = false; };
  }, [authorId, limit]);
  return state;
}

function StatusPill({ status }) {
  if (status !== "draft") return null;
  return (
    <span className="inline-block text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-700">
      Draft
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch { return ""; }
}

export default function ProfileArticlesSection({
  authorId,
  isOwner = false,
  handle,
  variant = "strip",
  limit,
}) {
  const { loading, articles, error } = useAuthorArticles(authorId, limit || (variant === "strip" ? 6 : 30));

  if (loading) {
    return (
      <div
        className="rounded-xl bg-white border p-5 text-slate-500 text-sm flex items-center gap-2"
        style={{ borderColor: "#E5E7EB" }}
        data-testid="profile-articles-loading"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Loading articles…
      </div>
    );
  }

  if (error) return null;

  if (articles.length === 0) {
    if (!isOwner) return null;
    return (
      <section
        className="rounded-xl bg-white border p-6 text-center"
        style={{ borderColor: "#E5E7EB" }}
        data-testid="profile-articles-empty"
      >
        <Newspaper className="w-8 h-8 mx-auto mb-2 text-slate-400" />
        <h3 className="text-base font-bold text-slate-900">You haven't written an article yet</h3>
        <p className="text-sm text-slate-600 mt-1">
          Publish your first story on SocialPulse.blog.
        </p>
        <Link
          to="/publisher/write"
          className="inline-flex items-center gap-1.5 mt-4 bg-slate-900 text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-slate-700"
          data-testid="profile-articles-write-cta"
        >
          <PenLine className="w-4 h-4" /> Start writing
        </Link>
      </section>
    );
  }

  if (variant === "strip") {
    // Horizontal preview strip for the main profile feed.
    return (
      <section
        className="rounded-xl bg-white border p-5"
        style={{ borderColor: "#E5E7EB" }}
        data-testid="profile-articles-strip"
      >
        <header className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Newspaper className="w-4 h-4 text-blue-700" />
            <h3 className="text-sm font-bold text-slate-900">
              {isOwner ? "Your articles" : "Articles"}
            </h3>
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              SocialPulse.blog
            </span>
          </div>
          <div className="flex items-center gap-2">
            {isOwner && (
              <Link
                to="/publisher/write"
                className="text-xs font-semibold text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
                data-testid="profile-articles-new"
              >
                <PenLine className="w-3 h-3" /> New
              </Link>
            )}
            <Link
              to={`/${handle}?tab=articles`}
              className="text-xs font-semibold text-blue-700 hover:text-blue-900 inline-flex items-center gap-1"
              data-testid="profile-articles-viewall"
            >
              View all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </header>
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {articles.slice(0, 6).map((a) => (
            <li key={a.id} data-testid={`profile-article-${a.id}`}>
              <Link
                to={`/blog/${a.slug}`}
                className="group block rounded-lg overflow-hidden border border-slate-200 hover:border-slate-400 hover:shadow-sm transition-all bg-white h-full flex flex-col"
              >
                {a.cover_image_url ? (
                  <img
                    src={a.cover_image_url}
                    alt=""
                    className="w-full h-32 object-cover bg-slate-100"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-32 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                    <Newspaper className="w-6 h-6 text-slate-400" />
                  </div>
                )}
                <div className="p-3 flex-1 flex flex-col">
                  <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                    <span className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">
                      {a.primary_category}
                    </span>
                    <StatusPill status={a.status} />
                  </div>
                  <h4 className="text-sm font-bold text-slate-900 leading-snug line-clamp-2 group-hover:text-blue-700">
                    {a.title}
                  </h4>
                  {a.summary && (
                    <p className="text-xs text-slate-600 mt-1 line-clamp-2">{a.summary}</p>
                  )}
                  <div className="mt-auto pt-2 flex items-center gap-2.5 text-[11px] text-slate-500">
                    <span className="inline-flex items-center gap-0.5">
                      <Eye className="w-3 h-3" /> {a.views_total.toLocaleString()}
                    </span>
                    <span className="inline-flex items-center gap-0.5">
                      <Heart className="w-3 h-3" /> {a.claps_total.toLocaleString()}
                    </span>
                    {a.published_at && (
                      <span className="ml-auto">{fmtDate(a.published_at)}</span>
                    )}
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </section>
    );
  }

  // grid variant — full-page dedicated tab
  return (
    <section
      className="rounded-xl bg-white border p-5"
      style={{ borderColor: "#E5E7EB" }}
      data-testid="profile-articles-grid"
    >
      <header className="flex items-center justify-between mb-5 pb-3 border-b border-slate-200">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Articles</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {articles.length} article{articles.length === 1 ? "" : "s"} on SocialPulse.blog
          </p>
        </div>
        {isOwner && (
          <Link
            to="/publisher/write"
            className="inline-flex items-center gap-1.5 bg-slate-900 text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-slate-700"
            data-testid="profile-articles-new-cta"
          >
            <PenLine className="w-4 h-4" /> New article
          </Link>
        )}
      </header>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {articles.map((a) => (
          <li key={a.id} data-testid={`profile-article-grid-${a.id}`}>
            <Link
              to={`/blog/${a.slug}`}
              className="group block rounded-xl overflow-hidden border border-slate-200 hover:border-slate-400 hover:shadow-md transition-all bg-white flex"
            >
              {a.cover_image_url ? (
                <img
                  src={a.cover_image_url}
                  alt=""
                  className="w-32 h-full object-cover bg-slate-100 shrink-0"
                  loading="lazy"
                />
              ) : (
                <div className="w-32 h-32 bg-gradient-to-br from-slate-100 to-slate-200 shrink-0 flex items-center justify-center">
                  <Newspaper className="w-6 h-6 text-slate-400" />
                </div>
              )}
              <div className="p-4 flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                  <span className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">
                    {a.primary_category}
                  </span>
                  <StatusPill status={a.status} />
                </div>
                <h3 className="text-base font-bold text-slate-900 leading-snug line-clamp-2 group-hover:text-blue-700">
                  {a.title}
                </h3>
                {a.summary && (
                  <p className="text-sm text-slate-600 mt-1 line-clamp-2">{a.summary}</p>
                )}
                <div className="mt-2 flex items-center gap-3 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1">
                    <Eye className="w-3 h-3" /> {a.views_total.toLocaleString()}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Heart className="w-3 h-3" /> {a.claps_total.toLocaleString()}
                  </span>
                  {a.published_at && (
                    <span className="ml-auto">{fmtDate(a.published_at)}</span>
                  )}
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
